import { Block, createScopedContext, evaluate } from "@lune-js/context";
import type { Context } from "@lune-js/context";
import { isArray, isObject, warn } from "@lune-js/utils";
import { walk } from ".";

const forAliasRE = /([\s\S]*?)\s+(?:in|of)\s+([\s\S]*)/;
const forIteratorRE = /,([^,}\]]*)(?:,([^,}\]]*))?$/;
const stripParensRE = /^\(|\)$/g;
const destructureRE = /^[{[]\s*((?:[\w_$]+\s*,?\s*)+)[\]}]$/;

type KeyToIndexMap = Map<any, number>;

/**
 * Implements list rendering behavior (`lu-for`). Parses iteration loop declarations,
 * extracts source array structures, dynamically traces indices, and provisions structural
 * template blocks cleanly using an optimized diff loop keyed on unique prototype lookups.
 * @param el - The blueprint list template element containing structural iteration markers.
 * @param exp - The source syntax sequence detailing key extraction parameters (e.g. `(item, index) in list`).
 * @param ctx - The base parent context block.
 * @returns The sequential sibling boundary node required to preserve layout continuity inside the walker.
 */
export function _for(el: Element, exp: string, ctx: Context): ChildNode | null | undefined {
  const inMatch = exp.match(forAliasRE);
  if (!inMatch) {
    if (import.meta.env.DEV) {
      warn(`invalid lu-for expression: "${exp}"`);
    }
    return;
  }

  const nextNode = el.nextSibling;
  const parent = el.parentElement;
  const anchor = new Text("");

  if (parent == null) return;
  parent.insertBefore(anchor, el);
  parent.removeChild(el);

  const sourceExp = inMatch[2].trim();
  let valueExp = inMatch[1].trim().replace(stripParensRE, "").trim();
  let destructureBindings: string[] | undefined;
  let isArrayDestructure = false;
  let indexExp: string | undefined;
  let objIndexExp: string | undefined;

  let keyAttr = "key";
  let keyExp =
    el.getAttribute(keyAttr) || el.getAttribute((keyAttr = ":key")) || el.getAttribute((keyAttr = "lu-bind:key"));
  if (keyExp) {
    el.removeAttribute(keyAttr);
    if (keyAttr === "key") keyExp = JSON.stringify(keyExp);
  }

  let match;
  if ((match = valueExp.match(forIteratorRE))) {
    valueExp = valueExp.replace(forIteratorRE, "").trim();
    indexExp = match[1].trim();
    if (match[2]) {
      objIndexExp = match[2].trim();
    }
  }

  if ((match = valueExp.match(destructureRE))) {
    destructureBindings = match[1].split(",").map((s) => s.trim());
    isArrayDestructure = valueExp[0] === "[";
  }

  let mounted = false;
  let blocks: Block[];
  let childCtxs: Context[];
  let keyToIndexMap: Map<any, number>;

  const createChildContexts = (source: unknown): [Context[], KeyToIndexMap] => {
    const map: KeyToIndexMap = new Map();
    const ctxs: Context[] = [];

    if (typeof source === "number") {
      for (let i = 0; i < source; i++) {
        ctxs.push(createChildContext(map, i + 1, i));
      }
    } else if (isArray(source)) {
      for (let i = 0; i < source.length; i++) {
        ctxs.push(createChildContext(map, source[i], i));
      }
    } else if (isObject(source)) {
      let i = 0;
      for (const key in source) {
        if (!(key in source)) continue;
        ctxs.push(createChildContext(map, source[key as keyof typeof source], i++, key));
      }
    }

    return [ctxs, map];
  };

  const createChildContext = (map: KeyToIndexMap, value: any, index: number, objKey?: string): Context => {
    const data: any = {};
    if (destructureBindings) {
      destructureBindings.forEach((b, i) => (data[b] = value[isArrayDestructure ? i : b]));
    } else {
      data[valueExp] = value;
    }

    if (objKey) {
      if (indexExp) {
        data[indexExp] = objKey;
      }

      if (objIndexExp) {
        data[objIndexExp] = index;
      }
    } else if (indexExp) {
      data[indexExp] = index;
    }

    const childCtx = createScopedContext(ctx, data);
    const key = keyExp ? evaluate(childCtx.scope, keyExp) : index;
    map.set(key, index);
    childCtx.key = key;
    return childCtx;
  };

  const mountBlock = (ctx: Context, ref: Node): Block => {
    const block = new Block(el, ctx, walk);
    block.key = ctx.key;
    block.insert(parent, ref);
    return block;
  };

  ctx.effect(() => {
    const source = evaluate(ctx.scope, sourceExp);
    const prevKeyToIndexMap = keyToIndexMap;
    [childCtxs, keyToIndexMap] = createChildContexts(source);

    if (!mounted) {
      blocks = childCtxs.map((s) => mountBlock(s, anchor));
      mounted = true;
    } else {
      for (let i = 0; i < blocks.length; i++) {
        if (!keyToIndexMap.has(blocks[i].key)) {
          blocks[i].remove();
        }
      }

      const nextBlocks: Block[] = [];
      let i = childCtxs.length;
      let nextBlock: Block | undefined;
      let prevMovedBlock: Block | undefined;
      while (i--) {
        const childCtx = childCtxs[i];
        const oldIndex = prevKeyToIndexMap.get(childCtx.key);
        let block;
        if (oldIndex == null) {
          // new
          block = mountBlock(childCtx, nextBlock ? nextBlock.el : anchor);
        } else {
          // update
          block = blocks[oldIndex];
          Object.assign(block.ctx.scope, childCtx.scope);
          if (oldIndex !== i) {
            // moved
            if (
              blocks[oldIndex + 1] !== nextBlock ||
              // If the next has moved, it must move too
              prevMovedBlock === nextBlock
            ) {
              prevMovedBlock = block;
              block.insert(parent, nextBlock ? nextBlock.el : anchor);
            }
          }
        }
        nextBlocks.unshift((nextBlock = block));
      }
      blocks = nextBlocks;
    }
  });

  return nextNode;
}
